import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import {
  NOT_UPSELL_LOCALIZATIONS,
  PaymentSystem,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { isRoverPartnership2UpsellEnabled } from './abtest/rover-partnership2/isRoverPartnership2UpsellEnabled'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isEnLocale = () => {
  const url = new URL(window.location.href)
  return url.searchParams.get('lang') === LOCALES.EN
}

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

export const getXStateConfigWoofz22V4 = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !hasEmail &&
            !hasSubscription &&
            !hasAccount &&
            hasAnswers &&
            pageIdFromUrl !== PageId.NEWSLETTER_URGE_22V4
          )
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return pageIdFromUrl === PageId.NEWSLETTER_URGE_22V4
        },
        target: PageId.EMAIL_22V4,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''

          return (
            pageIdFromUrl === PageId.ROVER_UPSELL_22V4 &&
            !isRoverPartnership2UpsellEnabled()
          )
        },
        target: PageId.DOWNLOAD_22V4,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''

          return (
            getHasAccount() &&
            isRoverPartnership2UpsellEnabled() &&
            pageIdFromUrl !== PageId.DOWNLOAD_22V4
          )
        },
        target: PageId.ROVER_UPSELL_22V4,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_22V4,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_22V4,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.LAST_CHANCE_OFFER_22V4 && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.WELCOME_TO_WOOFZ_22V4,
              },
              {
                target: PageId.SUBSCRIPTIONS_22V4,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_22V4 && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_22V4,
              },
            ],
            PREVIOUS_FLOW_STEP: {
              target: PageId.CANCEL_OFFER_22V4,
            },
          }),
          ...(page.pageId === PageId.CANCEL_OFFER_22V4 && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_22V4,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_22V4 && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return NOT_UPSELL_LOCALIZATIONS.includes(
                    languageFromUrl as string,
                  )
                },
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.UPSELL_22V4,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_22V4 && {
            NEXT_FLOW_STEP: [
              {
                cond: isEnLocale,
                target: PageId.UPSELL_FEEDING_GUIDE_22V4,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
          ...(page.pageId === PageId.UPSELL_FEEDING_GUIDE_22V4 && {
            NEXT_FLOW_STEP: PageId.UPSELL_EMOTIONAL_SUPPORT_22V4,
          }),
          ...(page.pageId === PageId.UPSELL_EMOTIONAL_SUPPORT_22V4 && {
            NEXT_FLOW_STEP: PageId.ACCOUNT_NOVE8,
          }),
          ...(page.pageId === PageId.ACCOUNT_NOVE8 && {
            NEXT_FLOW_STEP: [
              {
                cond: isRoverPartnership2UpsellEnabled,
                target: PageId.ROVER_UPSELL_22V4,
              },
              {
                target: PageId.DOWNLOAD_22V4,
              },
            ],
          }),
          ...(page.pageId === PageId.ROVER_UPSELL_22V4 && {
            NEXT_FLOW_STEP: {
              target: PageId.DOWNLOAD_22V4,
            },
          }),
        },
      },
    }),
    {},
  ),
  [PageId.DOWNLOAD_22V4]: {
    type: 'final',
  },
})
